import {AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, ViewChild} from '@angular/core';
import {Question} from '../../models/underwriter.model';
import {GlobalQuestionService} from '../../services/global-question.service';
import {QuestionFlowSessionQuery} from '../state/question-flow-session.query';

type ParsedTime = { hours: string; minutes: string };
type ParseTimeOptions = { allowHourOnly?: boolean };

@Component({
    selector: 'app-question-input-timepicker',
    templateUrl: './question-input-timepicker.component.html',
    styleUrls: ['./question-input-timepicker.component.scss'],
    standalone: false
})
export class QuestionInputTimepickerComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() hour = '';
  @Input() minute = '';
  @Input() minuteIncrement: number | string | null = null;
  @Input() disabledButton = true;
  @Input() question: Question;
  @Input() showLabel = true;
  @Output() questionAnswer = new EventEmitter<any>();

  @ViewChild('root', {static: false}) rootRef?: ElementRef<HTMLElement>;
  @ViewChild('dropdown', {static: false}) dropdownRef?: ElementRef<HTMLElement>;
  @ViewChild('hourScroll', {static: false}) hourScrollRef?: ElementRef<HTMLElement>;
  @ViewChild('minuteScroll', {static: false}) minuteScrollRef?: ElementRef<HTMLElement>;
  @ViewChild('input', {static: false}) inputRef?: ElementRef<HTMLInputElement>;

  readonly inputPattern = '^(?:\\d{1,2}(?:\\s*[:.\\-\\s]\\s*\\d{1,2})?|\\d{3,4})(?:\\s*[AaPp]\\.?\\s*[Mm]\\.?)?$';

  skipped = false;
  showSkip = false;
  parameter: any;
  sessionDetails: any;
  isOpen = false;
  isDropUp = false;
  inputValue = '';
  hasValue = false;

  hours: string[] = [];
  minutes: string[] = [];

  selectedHour = '12';
  selectedMinute = '00';

  maxMinute = 59;
  maxHour = 23;
  minHour = 0;
  minMinute = 0;

  private readonly defaultMinuteIncrement = 5;
  private activeMinuteIncrement = this.defaultMinuteIncrement;
  private valueBeforeOpen = '';
  private rangeMinMinutes = 0;
  private rangeMaxMinutes = 23 * 60 + 59;

  constructor(
    public globalQuestionService: GlobalQuestionService,
    @Optional() private questionFlowSessionQuery?: QuestionFlowSessionQuery
  ) {
    this.sessionDetails = this.questionFlowSessionQuery?.getValue?.();
    this.hours = Array.from({length: 24}, (_, index) => this.pad(index));
    this.minutes = this.buildMinutes(this.activeMinuteIncrement);
  }

  ngOnInit(): void {
    this.syncConfiguration();
    this.syncFromInputs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['question'] || changes['hour'] || changes['minute']) {
      this.syncConfiguration();
      this.syncFromInputs();
    }
  }

  ngAfterViewInit(): void {
    this.fetchClaimTimeRange();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen || !this.rootRef?.nativeElement) {
      return;
    }

    const target = event.target as Node | null;
    if (target && !this.rootRef.nativeElement.contains(target)) {
      this.commitAndClose();
    }
  }

  onBlur(): void {
    this.commitAndClose();
  }

  onInput(event: Event): void {
    const value = (event.target as HTMLInputElement)?.value ?? '';
    this.inputValue = value;

    const parsed = this.parseTime(value);
    if (parsed) {
      this.applyParsedTimeFromTyping(parsed);
    }
  }

  onEnter(event: Event): void {
    event.preventDefault();
    this.commitInput();
    this.openPicker();
  }

  openPicker(): void {
    if (!this.isOpen) {
      this.valueBeforeOpen = this.inputValue;
      this.isOpen = true;
      setTimeout(() => {
        this.repositionDropdown();
        this.centerSelectionInColumns({refreshSpacers: true});
      });
      return;
    }

    this.repositionDropdown();
    this.centerSelectionInColumns();
  }

  togglePicker(): void {
    if (this.isOpen) {
      this.commitAndClose();
      return;
    }

    this.openPicker();
  }

  onCancel(event?: Event): void {
    event?.preventDefault();
    this.inputValue = this.valueBeforeOpen;
    this.commitInput();
    this.closePicker();
    this.inputRef?.nativeElement.blur();
  }

  onApply(event?: Event): void {
    event?.preventDefault();
    this.commitInput();
    this.submitTime();
    this.closePicker();
    this.inputRef?.nativeElement.blur();
  }

  onSkipChange(): void {
    if (!this.question) {
      return;
    }

    if (this.skipped) {
      this.question.answer = 'skipped';
    } else {
      this.question.answer = '';
    }
    this.questionAnswer.emit(this.question);
  }

  onSelectHour(hour: string): void {
    if (this.isHourDisabled(hour)) {
      return;
    }

    this.selectedHour = hour;
    this.hasValue = true;
    this.ensureMinuteWithinRange();
    this.syncInputFromSelection();
    this.centerSelectionInColumns();
  }

  onSelectMinute(minute: string): void {
    if (this.isMinuteDisabled(minute)) {
      return;
    }

    this.selectedMinute = minute;
    this.hasValue = true;
    this.syncInputFromSelection();
    this.centerSelectionInColumns();
  }

  isHourDisabled(hour: string): boolean {
    return !this.minutes.some(minute => this.isWithinRange(+hour, +minute));
  }

  isMinuteDisabled(minute: string): boolean {
    return !this.isWithinRange(+this.selectedHour, +minute);
  }

  submitTime(): void {
    if (!this.hasValue || !this.selectedHour || !this.selectedMinute) {
      return;
    }

    const formattedTime = this.formatAnswer(this.selectedHour, this.selectedMinute);
    this.hasValue = true;
    this.inputValue = formattedTime;

    if (this.question) {
      this.question.answer = formattedTime;
      this.questionAnswer.emit(this.question);
      return;
    }

    this.questionAnswer.emit(formattedTime);
  }

  private syncConfiguration(): void {
    this.parameter = this.parseQuestionParameter();
    this.showSkip = this.parameter?.some((item: any) => item?.parameter === 'show_skip' && item?.value === '1') ?? false;

    const payloadMinuteIncrement = this.minuteIncrement;
    const parameterMinuteIncrement = this.parameter?.find((item: any) => item?.parameter === 'minute_increment')?.value;
    const minuteIncrementSource = payloadMinuteIncrement ?? parameterMinuteIncrement;
    const parsedIncrement = this.parseMinuteIncrement(minuteIncrementSource);
    if (parsedIncrement !== this.activeMinuteIncrement) {
      this.activeMinuteIncrement = parsedIncrement;
      this.minutes = this.buildMinutes(this.activeMinuteIncrement);
    }
  }

  private syncFromInputs(): void {
    if (this.question?.answer === 'skipped') {
      this.skipped = true;
      this.hasValue = false;
      this.inputValue = '';
      return;
    }

    if (this.question?.answer) {
      const parsed = this.parseTime(this.question.answer, {allowHourOnly: true});
      if (parsed) {
        this.applyParsedTime(parsed);
        return;
      }
    }

    if (this.hour && this.minute) {
      const parsed = this.parseLegacyInputs();
      if (parsed) {
        this.applyParsedTime(parsed);
        return;
      }
    }

    this.resetSelection();
    this.inputValue = '';
  }

  private parseLegacyInputs(): ParsedTime | null {
    return this.validateTime(this.hour, this.minute);
  }

  private parseQuestionParameter(): any[] {
    try {
      return JSON.parse(this.question?.parameter ?? 'null') ?? [];
    } catch (e) {
      return [];
    }
  }

  private parseMinuteIncrement(value: any): number {
    const parsed = parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > 60 || 60 % parsed !== 0) {
      return this.defaultMinuteIncrement;
    }

    return parsed;
  }

  private applyParsedTime(parsed: ParsedTime): void {
    const normalized = this.normalizeTime(parsed.hours, parsed.minutes);
    this.selectedHour = normalized.hours;
    this.selectedMinute = normalized.minutes;
    this.hasValue = true;
    this.enforceRange();
    this.syncInputFromSelection();
    this.centerSelectionInColumns();
  }

  private applyParsedTimeFromTyping(parsed: ParsedTime): void {
    const normalized = this.normalizeTime(parsed.hours, parsed.minutes);
    this.selectedHour = normalized.hours;
    this.selectedMinute = normalized.minutes;
    this.hasValue = true;
    this.enforceRange();

    if (this.isOpen) {
      this.centerSelectionInColumns();
    }
  }

  private syncInputFromSelection(): void {
    this.inputValue = this.formatAnswer(this.selectedHour, this.selectedMinute);
  }

  private commitInput(): void {
    const raw = this.inputValue.trim();

    if (!raw) {
      this.resetSelection();
      this.inputValue = '';
      return;
    }

    const parsed = this.parseTime(raw, {allowHourOnly: true});
    if (!parsed) {
      this.resetSelection();
      this.inputValue = '';
      return;
    }

    this.applyParsedTime(parsed);
  }

  private commitAndClose(): void {
    this.commitInput();
    this.submitTime();
    this.closePicker();
  }

  private closePicker(): void {
    this.isOpen = false;
    this.isDropUp = false;
  }

  private resetSelection(): void {
    this.selectedHour = '12';
    this.selectedMinute = '00';
    this.hasValue = false;
  }

  private fetchClaimTimeRange(): void {
    if (!this.globalQuestionService?.runCommandNew) {
      return;
    }

    const parameters = new Map<string, string>();
    parameters.set('session_id', this.sessionDetails?.session_id);

    this.globalQuestionService.runCommandNew('getClaimsTimeRange', parameters).subscribe({
      next: (resp: any) => {
        const timedRange = this.parseResponseTimeRange(resp);
        if (!timedRange) {
          return;
        }

        const min = timedRange[0]?.parameter === 'min_time' ? timedRange[0]?.value : '';
        const max = timedRange[1]?.parameter === 'max_time' ? timedRange[1]?.value : '';
        this.applyTimeRange(min, max);
      },
      error: (error: any) => {
        console.log(error);
      }
    });
  }

  private parseResponseTimeRange(resp: any): any[] | null {
    try {
      return JSON.parse(resp?.[0]?.time_range ?? 'null');
    } catch (e) {
      return null;
    }
  }

  private applyTimeRange(min: string, max: string): void {
    const parsedMin = min ? this.parseTime(min) : null;
    const parsedMax = max ? this.parseTime(max) : null;

    if (!parsedMin || !parsedMax) {
      return;
    }

    const minTotal = this.toMinutes(parsedMin.hours, parsedMin.minutes);
    const maxTotal = this.toMinutes(parsedMax.hours, parsedMax.minutes);

    if (minTotal > maxTotal) {
      return;
    }

    this.minHour = +parsedMin.hours;
    this.minMinute = +parsedMin.minutes;
    this.maxHour = +parsedMax.hours;
    this.maxMinute = +parsedMax.minutes;
    this.rangeMinMinutes = minTotal;
    this.rangeMaxMinutes = maxTotal;

    this.enforceRange();
    this.syncInputFromSelection();
    this.centerSelectionInColumns();
  }

  private enforceRange(): void {
    const currentTotal = this.toMinutes(this.selectedHour, this.selectedMinute);

    if (currentTotal < this.rangeMinMinutes) {
      this.selectedHour = this.pad(Math.floor(this.rangeMinMinutes / 60));
      this.selectedMinute = this.pad(this.rangeMinMinutes % 60);
    } else if (currentTotal > this.rangeMaxMinutes) {
      this.selectedHour = this.pad(Math.floor(this.rangeMaxMinutes / 60));
      this.selectedMinute = this.pad(this.rangeMaxMinutes % 60);
    }

    this.ensureMinuteWithinRange();
  }

  private ensureMinuteWithinRange(): void {
    const hour = +this.selectedHour;
    const allowedMinutes = this.minutes.filter(minute => this.isWithinRange(hour, +minute));

    if (!allowedMinutes.length) {
      return;
    }

    if (!allowedMinutes.includes(this.selectedMinute)) {
      this.selectedMinute = allowedMinutes[0];
    }
  }

  private isWithinRange(hour: number, minute: number): boolean {
    const total = (hour * 60) + minute;
    return total >= this.rangeMinMinutes && total <= this.rangeMaxMinutes;
  }

  private parseTime(value: string, options: ParseTimeOptions = {}): ParsedTime | null {
    const trimmed = value?.trim() ?? '';
    if (!trimmed) {
      return null;
    }

    const compact = trimmed.toLowerCase().replace(/\s+/g, ' ').trim();

    const meridiemMatch = /(a\.?\s*m\.?|p\.?\s*m\.?)$/i.exec(compact);
    const meridiem = meridiemMatch?.[1];
    const timePart = meridiemMatch ? compact.slice(0, meridiemMatch.index).trim() : compact;

    if (!timePart) {
      return null;
    }

    const separated = /^(\d{1,2})\s*[:.\-\s]\s*(\d{1,2})$/.exec(timePart);
    if (separated) {
      return meridiem
        ? this.parse12Hour(separated[1], separated[2], meridiem)
        : this.validateTime(separated[1], separated[2]);
    }

    const compactDigits = /^(\d{3,4})$/.exec(timePart.replace(/\s+/g, ''));
    if (compactDigits) {
      const digits = compactDigits[1];
      const hours = digits.length === 3 ? digits.slice(0, 1) : digits.slice(0, 2);
      const minutes = digits.slice(-2);

      return meridiem
        ? this.parse12Hour(hours, minutes, meridiem)
        : this.validateTime(hours, minutes);
    }

    if (meridiem) {
      const hourOnlyMeridiem = /^(\d{1,2})$/.exec(timePart);
      if (hourOnlyMeridiem) {
        return this.parse12Hour(hourOnlyMeridiem[1], undefined, meridiem);
      }
    }

    if (options.allowHourOnly) {
      const hourOnly = /^(\d{1,2})$/.exec(timePart);
      if (hourOnly) {
        return this.validateTime(hourOnly[1], '0');
      }
    }

    return null;
  }

  private validateTime(hours: string, minutes: string): ParsedTime | null {
    const hourNumber = parseInt(hours, 10);
    const minuteNumber = parseInt(minutes, 10);

    if (hourNumber < 0 || hourNumber > 23 || minuteNumber < 0 || minuteNumber > 59) {
      return null;
    }

    return {
      hours: this.pad(hourNumber),
      minutes: this.pad(minuteNumber),
    };
  }

  private parse12Hour(hours: string, minutes: string | undefined, meridiem: string): ParsedTime | null {
    let hourNumber = parseInt(hours, 10);
    const minuteNumber = minutes === undefined || minutes === '' ? 0 : parseInt(minutes, 10);

    if (hourNumber < 1 || hourNumber > 12 || minuteNumber < 0 || minuteNumber > 59) {
      return null;
    }

    const mer = meridiem.toLowerCase().replace(/\./g, '').replace(/\s/g, '');
    if (mer === 'am') {
      if (hourNumber === 12) {
        hourNumber = 0;
      }
    } else if (mer === 'pm') {
      if (hourNumber !== 12) {
        hourNumber += 12;
      }
    } else {
      return null;
    }

    return {
      hours: this.pad(hourNumber),
      minutes: this.pad(minuteNumber),
    };
  }

  private normalizeTime(hours: string, minutes: string): ParsedTime {
    const snappedMinutes = this.snapMinute(minutes, this.activeMinuteIncrement);
    return {
      hours: this.pad(parseInt(hours, 10)),
      minutes: snappedMinutes,
    };
  }

  private snapMinute(minutes: string, increment: number): string {
    const minuteNumber = parseInt(minutes, 10);
    const snapped = Math.round(minuteNumber / increment) * increment;
    const clamped = Math.min(snapped, 60 - increment);
    return this.pad(clamped);
  }

  private buildMinutes(increment: number): string[] {
    const minuteValues: string[] = [];
    for (let minute = 0; minute < 60; minute += increment) {
      minuteValues.push(this.pad(minute));
    }
    return minuteValues;
  }

  private centerSelection(): void {
    this.centerSelectionInColumns();
  }

  private centerSelectionInColumns(options: {refreshSpacers?: boolean} = {}): void {
    const center = (): void => {
      this.centerOption(this.hourScrollRef, this.selectedHour);
      this.centerOption(this.minuteScrollRef, this.selectedMinute);
    };

    if (!options.refreshSpacers && this.hasValue) {
      requestAnimationFrame(center);
      return;
    }

    let pending = 2;
    const onReady = (): void => {
      pending -= 1;
      if (pending === 0) {
        center();
      }
    };

    this.applyScrollSpacers(this.hourScrollRef, onReady);
    this.applyScrollSpacers(this.minuteScrollRef, onReady);
  }

  private applyScrollSpacers(scrollRef: ElementRef<HTMLElement> | undefined, onReady: () => void): void {
    const scrollEl = scrollRef?.nativeElement;
    if (!scrollEl) {
      onReady();
      return;
    }

    const list = scrollEl.querySelector('.custom-timepicker__list') as HTMLElement | null;
    const firstOption = list?.querySelector('.custom-timepicker__option') as HTMLElement | null;
    if (!list || !firstOption) {
      onReady();
      return;
    }

    list.querySelectorAll('.custom-timepicker__spacer').forEach((el) => el.remove());

    const apply = (): void => {
      const optionHeight = firstOption.offsetHeight;
      const spacerHeight = Math.max(0, (scrollEl.clientHeight - optionHeight) / 2);
      const topSpacers = this.createSpacers(spacerHeight, optionHeight);
      const bottomSpacers = this.createSpacers(spacerHeight, optionHeight);

      for (let i = topSpacers.length - 1; i >= 0; i--) {
        list.insertBefore(topSpacers[i], list.firstChild);
      }
      for (const spacer of bottomSpacers) {
        list.appendChild(spacer);
      }
    };

    apply();
    requestAnimationFrame(() => {
      apply();
      onReady();
    });
  }

  private createSpacers(totalHeight: number, optionHeight: number): HTMLElement[] {
    const spacers: HTMLElement[] = [];
    let remaining = totalHeight;

    while (remaining > 0) {
      const height = Math.min(optionHeight, remaining);
      const spacer = document.createElement('li');
      spacer.className = 'custom-timepicker__spacer';
      spacer.setAttribute('aria-hidden', 'true');
      spacer.style.height = `${height}px`;
      spacers.push(spacer);
      remaining -= height;
    }

    return spacers;
  }

  private centerOption(scrollRef: ElementRef<HTMLElement> | undefined, value: string): void {
    const scrollEl = scrollRef?.nativeElement;
    if (!scrollEl) {
      return;
    }

    const option = scrollEl.querySelector(`[data-value="${value}"]`) as HTMLElement | null;
    if (!option) {
      return;
    }

    const scrollTop = option.offsetTop - (scrollEl.clientHeight / 2) + (option.offsetHeight / 2);
    scrollEl.scrollTop = Math.max(0, scrollTop);
  }

  private repositionDropdown(): void {
    const inputEl = this.inputRef?.nativeElement;
    const dropdownEl = this.dropdownRef?.nativeElement;
    if (!inputEl || !dropdownEl) {
      return;
    }

    this.isDropUp = false;

    const gap = 4;
    const inputRect = inputEl.getBoundingClientRect();
    const dropdownHeight = dropdownEl.offsetHeight;
    const spaceBelow = window.innerHeight - inputRect.bottom - gap;
    const spaceAbove = inputRect.top - gap;
    const fitsBelow = spaceBelow >= dropdownHeight;
    const fitsAbove = spaceAbove >= dropdownHeight;

    if ((!fitsBelow && fitsAbove) || (!fitsBelow && !fitsAbove && spaceAbove > spaceBelow)) {
      this.isDropUp = true;
    }
  }

  private formatAnswer(hours: string, minutes: string): string {
    return `${this.pad(hours)}:${this.pad(minutes)}`;
  }

  private toMinutes(hours: string, minutes: string): number {
    return (parseInt(hours, 10) * 60) + parseInt(minutes, 10);
  }

  private pad(value: number | string): string {
    return String(value).padStart(2, '0');
  }
}